import type { EvalVideo, UpdateEvalVideoParams } from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";

import { readVideoDuration, uploadToPresignedUrl } from "./upload";

/** The editable fields, with the start time as ISO text over the wire. */
export type GoldVideoPatch = Omit<UpdateEvalVideoParams, "startTime"> & {
	startTime?: string;
};

/** Gemini's suggested gold standard fields, for the edit form to pre-fill. */
export interface GoldVideoSuggestion {
	description: string;
	severity: number;
	title: string;
}

/** The whole gold standard library, newest first. */
export const useGoldVideos = () =>
	useQuery({
		queryFn: async () => {
			const response = await apiClient.get<EvalVideo[]>(
				ENDPOINTS.shadow.goldVideos.list,
			);
			return response.data;
		},
		queryKey: queryKeys.goldVideos.all,
	});

/**
 * A signed playback URL, held for most of its hour. Not retried: a clip still
 * uploading has no object yet, and the player says so.
 */
export const useGoldVideoUrl = (videoId: string | undefined) =>
	useQuery({
		enabled: Boolean(videoId),
		gcTime: 0,
		queryFn: async () => {
			const response = await apiClient.get<{ url: string }>(
				ENDPOINTS.shadow.goldVideos.url(videoId ?? ""),
			);
			return response.data.url;
		},
		queryKey: queryKeys.goldVideos.url(videoId ?? ""),
		retry: false,
		staleTime: 50 * 60 * 1000,
	});

/**
 * Upload files one after another: read each clip's length, create its
 * record, then PUT the clip to the presigned URL.
 */
export const useUploadGoldVideos = (handlers: {
	onFileDone: (index: number) => void;
	onProgress: (percent: number) => void;
}) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (files: File[]) => {
			for (const [index, file] of files.entries()) {
				handlers.onProgress(0);
				const durationSeconds = await readVideoDuration(file);
				const response = await apiClient.post<{
					presignedUrl: string;
					video: EvalVideo;
				}>(ENDPOINTS.shadow.goldVideos.list, {
					durationSeconds,
					filename: file.name,
				});
				await uploadToPresignedUrl(
					file,
					response.data.presignedUrl,
					handlers.onProgress,
				);
				handlers.onFileDone(index);
			}
		},
		// A failure part way still leaves the earlier files saved
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.goldVideos.all }),
	});
};

export const useUpdateGoldVideo = (videoId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (patch: GoldVideoPatch) => {
			const response = await apiClient.patch<EvalVideo>(
				ENDPOINTS.shadow.goldVideos.video(videoId),
				patch,
			);
			return response.data;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.goldVideos.all }),
	});
};

/** Blocked by the API while a use case or eval result still uses the video. */
export const useDeleteGoldVideo = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (videoId: string) => {
			await apiClient.delete(ENDPOINTS.shadow.goldVideos.video(videoId));
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.goldVideos.all }),
	});
};

/** A suggestion only: nothing is saved until the form is. */
export const useSuggestGoldVideo = (videoId: string) =>
	useMutation({
		mutationFn: async () => {
			const response = await apiClient.post<GoldVideoSuggestion>(
				ENDPOINTS.shadow.goldVideos.analyze(videoId),
			);
			return response.data;
		},
	});
